import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

// About.jsx — Manifest + Owl identity
const About = (() => {
  const { Diamond, Bolt, Star, Heart } = PixelIcons;

  const aboutStyles = {
    section: {
      padding: "112px 0",
      borderTop: "1px solid var(--border-soft)",
      borderBottom: "1px solid var(--border-soft)",
      background:
        "linear-gradient(180deg, rgba(67,0,95,0.10) 0%, transparent 100%), var(--bg-app)",
    },
    container: {
      maxWidth: 1280,
      margin: "0 auto",
      padding: "0 32px",
      display: "grid",
      gridTemplateColumns: "0.9fr 1.1fr",
      gap: 64,
      alignItems: "center",
    },
    emblemWrap: {
      position: "relative",
      aspectRatio: "1/1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    aura: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(circle at center, rgba(253,20,145,0.35) 0%, transparent 55%), radial-gradient(circle at 30% 30%, rgba(0,232,203,0.18) 0%, transparent 50%)",
      filter: "blur(20px)",
    },
    ring: {
      position: "absolute",
      border: "1px solid",
      borderRadius: 0,
      transform: "rotate(45deg)",
    },
    owl: {
      width: "62%",
      height: "62%",
      objectFit: "contain",
      filter: "drop-shadow(0 0 40px rgba(253,20,145,0.5))",
      position: "relative",
      zIndex: 2,
      animation: "floatY 6s ease-in-out infinite",
    },
    cornerBadge: {
      position: "absolute",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px",
      letterSpacing: "0.10em",
      color: "var(--ragross-cyan)",
      background: "var(--ragross-purple-night)",
      border: "1px solid rgba(0,232,203,0.4)",
      padding: "6px 10px",
      borderRadius: 2,
      zIndex: 3,
    },
    sectionTag: {
      display: "inline-flex",
      alignItems: "center",
      gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px",
      letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: {
      font: "var(--type-display-lg)",
      letterSpacing: "-0.015em",
      lineHeight: 1.05,
      color: "#fff",
      marginTop: 18,
      marginBottom: 0,
    },
    titleAccent: { color: "var(--ragross-cyan)" },
    body: {
      font: "var(--type-body-lg)",
      color: "var(--fg-2)",
      marginTop: 22,
      maxWidth: 560,
    },
    pillars: {
      marginTop: 36,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 16,
    },
    pillar: {
      padding: 16,
      background: "var(--bg-surface)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      transition: "border-color 200ms, transform 200ms var(--ease-arcade)",
    },
    pillarHead: {
      display: "flex", alignItems: "center", gap: 10,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "10px",
      letterSpacing: "0.10em",
    },
    pillarBody: {
      font: "var(--type-body-sm)",
      color: "var(--fg-2)",
    },
    motto: {
      marginTop: 32,
      padding: "16px 18px",
      borderLeft: "3px solid var(--ragross-gold)",
      background: "rgba(253,177,21,0.05)",
      fontFamily: "'VT323', monospace",
      fontSize: 22,
      lineHeight: 1.35,
      color: "#fff",
      maxWidth: 560,
    },
    mottoAttr: {
      marginTop: 8,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px",
      letterSpacing: "0.18em",
      color: "var(--ragross-gold)",
    },
  };

  function Pillar({ icon, color, head, body }) {
    const [h, setH] = React.useState(false);
    return (
      <div
        style={{
          ...aboutStyles.pillar,
          borderColor: h ? color : aboutStyles.pillar.borderColor,
          transform: h ? "translate(-2px,-2px)" : "none",
          boxShadow: h ? `4px 4px 0 ${color}` : "none",
        }}
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
      >
        <div style={{ ...aboutStyles.pillarHead, color }}>
          {icon} {head}
        </div>
        <div style={aboutStyles.pillarBody}>{body}</div>
      </div>
    );
  }

  function About() {
    const t = useT();

    return (
      <section id="about" style={aboutStyles.section} data-screen-label="02 Nosotros">
        <div style={aboutStyles.container} className="responsive-grid-2">
          <div style={aboutStyles.emblemWrap}>
            <div style={aboutStyles.aura}></div>
            <div style={{
              ...aboutStyles.ring, width: "92%", height: "92%",
              borderColor: "rgba(253,20,145,0.18)",
            }}></div>
            <div style={{
              ...aboutStyles.ring, width: "72%", height: "72%",
              borderColor: "rgba(0,232,203,0.22)",
            }}></div>
            <div style={{
              ...aboutStyles.ring, width: "52%", height: "52%",
              borderColor: "rgba(253,177,21,0.30)",
            }}></div>

            <img
              src="/assets/logos/ragross-owl-emblem.png"
              alt="Ragross owl"
              style={aboutStyles.owl}
            />

            <span style={{ ...aboutStyles.cornerBadge, top: "8%", left: "6%" }}>
              ▸ {t("about.emblem")}
            </span>
            <span style={{ ...aboutStyles.cornerBadge, bottom: "8%", right: "6%", color: "var(--ragross-gold)", borderColor: "rgba(253,177,21,0.4)" }}>
              {t("about.since")}
            </span>
          </div>

          <div>
            <div style={aboutStyles.sectionTag}>
              <span>{t("about.tag")}</span>
              <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
            </div>

            <h2 style={aboutStyles.title} className="section-title">
              {t("about.title1")}
              <br/>
              <span style={aboutStyles.titleAccent}>{t("about.title2")}</span>
            </h2>

            <p style={aboutStyles.body}>{t("about.body")}</p>

            <div style={aboutStyles.pillars} className="responsive-grid-2">
              <Pillar icon={<Diamond size={14}/>} color="var(--ragross-cyan)"   head={t("about.pillar1.h")} body={t("about.pillar1.b")}/>
              <Pillar icon={<Bolt    size={14}/>} color="var(--ragross-magenta)" head={t("about.pillar2.h")} body={t("about.pillar2.b")}/>
              <Pillar icon={<Star    size={14}/>} color="var(--ragross-gold)"    head={t("about.pillar3.h")} body={t("about.pillar3.b")}/>
              <Pillar icon={<Heart   size={14}/>} color="var(--ragross-green)"   head={t("about.pillar4.h")} body={t("about.pillar4.b")}/>
            </div>

            <div style={aboutStyles.motto}>
              {t("about.motto")}
              <div style={aboutStyles.mottoAttr}>{t("about.mottoAttr")}</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return About;
})();

export default About;
